/** Strip Mongo/internal keys from nested objects sent to PUT /customers/:id */
export function stripInternalKeys(obj: unknown, keysToStrip: string[] = ['_id', '__v']): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (obj instanceof Date) {
    return isNaN(obj.getTime()) ? null : new Date(obj.getTime());
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => stripInternalKeys(item, keysToStrip));
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  const clone: Record<string, unknown> = {};
  Object.keys(obj as Record<string, unknown>).forEach((key) => {
    if (keysToStrip.indexOf(key) !== -1) {
      return;
    }
    clone[key] = stripInternalKeys((obj as Record<string, unknown>)[key], keysToStrip);
  });
  return clone;
}

/** Returns v as a non-empty ISO date string, or null if unset / invalid. */
export function asDateString(v: unknown): string | null {
  if (v === null || v === undefined) {
    return null;
  }
  if (v instanceof Date) {
    return isNaN(v.getTime()) ? null : v.toISOString();
  }
  if (typeof v === 'number' && !isNaN(v)) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) {
      return null;
    }
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? s : parsed.toISOString();
  }
  return null;
}

export interface MemberUpdatePayloadOptions {
  modifiedBy?: string;
  type?: string;
  includeCompanyBranch?: boolean;
  /** When true, always include customerstrainer from member (trainer details tab). */
  includeCustomerTrainer?: boolean;
}

/**
 * Full customerstrainer block for PUT (legacy postedCustomer.customerstrainer parity).
 */
export function buildCustomerTrainerPayload(
  customerstrainer: unknown,
): Record<string, unknown> | undefined {
  if (!customerstrainer || typeof customerstrainer !== 'object') {
    return undefined;
  }

  const raw = customerstrainer as Record<string, unknown>;
  const dueDateRaw = raw.dueDate;
  const ct = stripInternalKeys(customerstrainer) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};

  const copyStr = (key: string) => {
    const v = ct[key];
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      payload[key] = String(v);
    }
  };

  copyStr('trainerId');
  copyStr('name');
  copyStr('totalFees');
  copyStr('gymShare');
  copyStr('trainerShare');
  copyStr('sessionCount');
  copyStr('description');

  if (ct.remainingSessionCount !== undefined && ct.remainingSessionCount !== null) {
    payload.remainingSessionCount = Number(ct.remainingSessionCount);
  }
  if (ct.gymSharePercentage !== undefined && ct.gymSharePercentage !== null) {
    payload.gymSharePercentage = Number(ct.gymSharePercentage);
  }
  if (ct.trainerSharePercentage !== undefined && ct.trainerSharePercentage !== null) {
    payload.trainerSharePercentage = Number(ct.trainerSharePercentage);
  }

  const dueDate = asDateString(dueDateRaw ?? ct.dueDate);
  if (dueDate) {
    payload.dueDate = dueDate;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
}

/**
 * Build body for PUT /api/v1/branches/:branchId/customers/:id (UpdateCustomerDto).
 * Omits read-only / server-managed fields that trigger forbidNonWhitelisted validation.
 */
export function buildMemberUpdatePayload(
  member: object,
  options: MemberUpdatePayloadOptions = {},
): Record<string, unknown> {
  const m = (member || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};

  if (m.name) payload.name = m.name;
  const dob = asDateString(m.dateOfBirth);
  if (dob) payload.dateOfBirth = dob;
  if (m.email !== undefined && m.email !== null) {
    payload.email = String(m.email).trim();
  }
  if (m.gender) payload.gender = m.gender;
  if (m.profile !== undefined && m.profile !== null) payload.profile = m.profile;
  if (m.studentCard) payload.studentCard = m.studentCard;
  if (m.noteDetails) payload.noteDetails = m.noteDetails;

  if (m.packageId) payload.packageId = m.packageId;
  const joiningDate = asDateString(m.joiningDate);
  if (joiningDate) payload.joiningDate = joiningDate;
  if (m.bloodGroup !== undefined && m.bloodGroup !== null) payload.bloodGroup = m.bloodGroup;
  if (m.registerationFee !== undefined && m.registerationFee !== null) {
    payload.registerationFee = String(m.registerationFee);
  }
  if (m.trainingFee !== undefined && m.trainingFee !== null) {
    payload.trainingFee = String(m.trainingFee);
  }
  if (m.monthFee !== undefined && m.monthFee !== null) {
    payload.monthFee = String(m.monthFee);
  }

  if (m.description) payload.description = m.description;

  if (m.contactDetail) {
    payload.contactDetail = stripInternalKeys(m.contactDetail);
  }
  if (m.emergencyContact) {
    payload.emergencyContact = stripInternalKeys(m.emergencyContact);
  }
  const settingsDueDate = asDateString(
    (m.settings as Record<string, unknown> | undefined)?.dueDate,
  );
  if (settingsDueDate) {
    payload.settings = { dueDate: settingsDueDate };
  }

  if (m.question1) payload.question1 = m.question1;
  if (m.question2) payload.question2 = m.question2;
  if (m.question3) payload.question3 = m.question3;
  if (m.gPTQuestion) payload.gPTQuestion = m.gPTQuestion;
  if (m.profession) payload.profession = m.profession;

  if (
    options.includeCustomerTrainer === true ||
    m.question3 === 'YES' ||
    m.customerstrainer
  ) {
    const trainerPayload = buildCustomerTrainerPayload(m.customerstrainer);
    if (trainerPayload) {
      payload.customerstrainer = trainerPayload;
    }
  }

  if (m.cardNo !== undefined && m.cardNo !== null) {
    payload.cardNo = String(m.cardNo).trim();
  }
  if (m.packageType) payload.packageType = m.packageType;
  if (m.packageName) payload.packageName = m.packageName;

  if (m.relatedMember) {
    payload.relatedMember = stripInternalKeys(m.relatedMember);
  }

  if (m.CNIC) payload.CNIC = m.CNIC;
  if (m.CNICImageUrl) payload.CNICImageUrl = m.CNICImageUrl;
  if (m.acknowledgedImage && m.acknowledgedImage !== 'undefined') {
    payload.acknowledgedImage = m.acknowledgedImage;
  }
  if (m.height) payload.height = m.height;
  if (m.weight) payload.weight = m.weight;
  if (m.referralSource) payload.referralSource = m.referralSource;
  if (m.referralOther) payload.referralOther = m.referralOther;
  if (m.enquiryId) payload.enquiryId = m.enquiryId;

  if (m.feeDetails) {
    payload.feeDetails = stripInternalKeys(m.feeDetails);
  }
  if (m.attendance) {
    payload.attendance = stripInternalKeys(m.attendance);
  }

  if (m.tagNumber) payload.tagNumber = m.tagNumber;

  if (options.includeCompanyBranch) {
    if (m.company) payload.company = m.company;
    if (m.branch) payload.branch = m.branch;
  }

  if (options.modifiedBy) {
    payload.modifiedBy = options.modifiedBy;
  } else if (m.modifiedBy) {
    payload.modifiedBy = m.modifiedBy;
  }

  if (options.type) {
    payload.type = options.type;
  } else if (m.type && typeof m.type === 'string') {
    payload.type = m.type;
  }

  return payload;
}

export function customerActivateMemberUrl(branchId: string, customerId: string): string {
  return `branches/${branchId}/customers/${customerId}/actions/activate-member`;
}

export function customerUpdateUrl(branchId: string, customerId: string): string {
  return `branches/${branchId}/customers/${customerId}`;
}

/** GET/PUT/DELETE .../branches/:branchId/customers/:id */
export function customerGetUrl(branchId: string, customerId: string): string {
  return customerUpdateUrl(branchId, customerId);
}

export function customerCreateUrl(branchId: string): string {
  return `branches/${branchId}/customers`;
}

/** Body for POST .../branches/:branchId/customers (CreateCustomerDto). */
export function buildCreateCustomerPayload(member: object): Record<string, unknown> {
  const payload = buildMemberUpdatePayload(member, { includeCompanyBranch: true });
  delete payload.modifiedBy;
  delete payload.type;
  const m = (member || {}) as Record<string, unknown>;
  if (m.createdBy) {
    payload.createdBy = m.createdBy;
  }
  if (m.branchName !== undefined && m.branchName !== null) {
    payload.branchName = String(m.branchName).trim();
  }
  return payload;
}

export function customerFreezeMemberUrl(branchId: string, customerId: string): string {
  return `branches/${branchId}/customers/${customerId}/actions/freeze`;
}

/** Body for PUT .../customers/:id/actions/freeze (FreezeMemberPayload). */
export function buildFreezeMemberPayload(
  member: object,
  currentUserData?: { name?: string },
): Record<string, unknown> {
  const m = (member || {}) as Record<string, unknown>;
  const fd = (m.freezeDetail || {}) as Record<string, unknown>;
  const settings = (m.settings || {}) as Record<string, unknown>;
  const dueDate = asDateString(settings.dueDate) ?? settings.dueDate;

  const payload: Record<string, unknown> = {
    tagNumber: m.tagNumber,
    company: m.company,
    branch: m.branch,
    settings: dueDate != null ? { dueDate } : {},
    freezeDetail: {
      ...(fd.daysLeft != null && fd.daysLeft !== '' ? { daysLeft: String(fd.daysLeft) } : {}),
      ...(fd.totalFreezingDays != null ? { totalFreezingDays: fd.totalFreezingDays } : {}),
      ...(fd.extraDays != null ? { extraDays: fd.extraDays } : {}),
    },
  };

  if (m.monthFee !== undefined && m.monthFee !== null) {
    payload.monthFee = String(m.monthFee);
  }

  const actorName = currentUserData?.name ?? (m.currentUserData as { name?: string } | undefined)?.name;
  if (actorName) {
    payload.currentUserData = { name: actorName };
  }

  return payload;
}

export function customerDefreezeMemberUrl(branchId: string, customerId: string): string {
  return `branches/${branchId}/customers/${customerId}/actions/defreeze`;
}

export function customerCancelFreezeUrl(branchId: string, customerId: string): string {
  return `branches/${branchId}/customers/${customerId}/actions/cancel-freeze`;
}

/** Body for PUT .../actions/cancel-freeze (FreezeMemberPayload). */
export function buildCancelFreezePayload(
  member: object,
  currentUserData?: { name?: string },
): Record<string, unknown> {
  const m = (member || {}) as Record<string, unknown>;
  const settings = (m.settings || {}) as Record<string, unknown>;
  const dueDate = asDateString(settings.dueDate) ?? settings.dueDate;

  const payload: Record<string, unknown> = {
    tagNumber: m.tagNumber,
    company: m.company,
    branch: m.branch,
    settings: dueDate != null ? { dueDate } : {},
    freezeDetail: { daysLeft: '0' },
  };

  if (m.monthFee !== undefined && m.monthFee !== null) {
    payload.monthFee = String(m.monthFee);
  }

  const actorName = currentUserData?.name ?? (m.currentUserData as { name?: string } | undefined)?.name;
  if (actorName) {
    payload.currentUserData = { name: actorName };
  }

  return payload;
}

export function customerCreateFreezeScheduleUrl(
  branchId: string,
  customerId: string,
): string {
  return `branches/${branchId}/customers/${customerId}/actions/create-freeze-schedule`;
}

export function customerCancelFreezeScheduleUrl(
  branchId: string,
  customerId: string,
  requestId: string,
): string {
  return `branches/${branchId}/customers/${customerId}/actions/cancel-freeze-schedule/${requestId}`;
}

/** Body for PUT .../actions/cancel-freeze-schedule/:requestId (CancelFreezeScheduleDto). */
export function buildCancelFreezeSchedulePayload(actorName?: string): Record<string, unknown> {
  return actorName ? { cancelledBy: actorName } : {};
}

/** Body for PUT .../actions/create-freeze-schedule (CreateFreezeScheduleDto). */
export function buildCreateFreezeSchedulePayload(freezeRequest: {
  days: string;
  startDate: string;
  endDate: string;
}): Record<string, unknown> {
  return {
    freezeRequest: {
      days: freezeRequest.days,
      startDate: freezeRequest.startDate,
      endDate: freezeRequest.endDate,
    },
  };
}

export function customerIsSyncedUrl(branchId: string, customerId: string): string {
  return `branches/${branchId}/customers/${customerId}/actions/is-synced`;
}

export function customerCreateOnlineBookingInviteUrl(
  branchId: string,
  customerId: string,
): string {
  return `branches/${branchId}/customers/${customerId}/actions/create-online-booking-invite`;
}

export function customerOnlineBookingInviteStatusUrl(
  branchId: string,
  customerId: string,
): string {
  return `branches/${branchId}/customers/${customerId}/actions/online-booking-invite-status`;
}

export function walletUpdateAmountUrl(branchId: string): string {
  return `branches/${branchId}/wallets/actions/update-amount`;
}

export function walletCheckBalanceUrl(branchId: string): string {
  return `branches/${branchId}/wallets/actions/check-balance`;
}

/** Body for POST .../wallets/actions/update-amount (UpdateWalletAmountDto). */
export function buildUpdateWalletAmountPayload(params: {
  memberId: string;
  customerName: string;
  membershipNo: string;
  updateWalletAmount: number;
  previousBalance: number | string;
  createdBy: string;
}): Record<string, unknown> {
  return {
    memberId: params.memberId,
    customerName: params.customerName,
    membershipNo: params.membershipNo,
    updateWalletAmount: params.updateWalletAmount,
    walletDetail: {
      previousBalance: String(params.previousBalance),
      createdBy: params.createdBy,
    },
  };
}

/** Body for PUT .../customers/:id/actions/defreeze (DefreezeMemberPayload). */
export function buildDefreezeMemberPayload(
  member: object,
  currentUserData?: { name?: string },
): Record<string, unknown> {
  const m = (member || {}) as Record<string, unknown>;
  const fd = (m.freezeDetail || {}) as Record<string, unknown>;
  const settings = (m.settings || {}) as Record<string, unknown>;
  const dueDate = asDateString(settings.dueDate) ?? (settings.dueDate != null ? String(settings.dueDate) : '');

  const payload: Record<string, unknown> = {
    tagNumber: m.tagNumber,
    company: m.company,
    branch: m.branch,
    settings: { dueDate },
    freezeDetail: {
      ...(fd.daysLeft != null && fd.daysLeft !== '' ? { daysLeft: String(fd.daysLeft) } : {}),
      ...(fd.totalFreezingDays != null ? { totalFreezingDays: fd.totalFreezingDays } : {}),
      ...(fd.extraDays != null ? { extraDays: fd.extraDays } : {}),
    },
  };

  if (m.monthFee !== undefined && m.monthFee !== null) {
    payload.monthFee = String(m.monthFee);
  }

  const actorName = currentUserData?.name ?? (m.currentUserData as { name?: string } | undefined)?.name;
  if (actorName) {
    payload.currentUserData = { name: actorName };
  }

  return payload;
}
